"use client";

import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import styles from './Navbar.module.css';
import { useLanguage } from '@/context/LanguageContext';
import { useSavedVacancies } from '@/context/SavedVacanciesContext';
import { useSavedCandidates } from '@/context/SavedCandidatesContext';
import { getRecentJobsFromSubscribedCompanies } from '@/app/actions/notificationActions';
import { getUserProfile } from '@/app/actions/authActions';
import { TranslatableLabel } from '@/components/TranslatableText';

import { useSession, signOut } from 'next-auth/react';

export default function Navbar() {
  const router = useRouter();
  const { data: session, status } = useSession();
  const { language, setLanguage, t } = useLanguage();
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('User');
  const [userImage, setUserImage] = useState('');
  const [userRole, setUserRole] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadApplications, setUnreadApplications] = useState<any[]>([]);
  const [adminNotifications, setAdminNotifications] = useState<any[]>([]);
  const [userClaimNotifications, setUserClaimNotifications] = useState<any[]>([]);
  const [selectedClaim, setSelectedClaim] = useState<any>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const mobileProfileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setLangDropdownOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
      if (
        (profileRef.current && !profileRef.current.contains(event.target as Node)) &&
        (mobileProfileRef.current && !mobileProfileRef.current.contains(event.target as Node))
      ) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  let savedVacancies: any[] = [];
  try {
    const context = useSavedVacancies();
    savedVacancies = context.savedVacancies;
  } catch (e) {}

  let savedCandidates: any[] = [];
  try {
    const candidateContext = useSavedCandidates();
    savedCandidates = candidateContext.savedCandidates;
  } catch (e) {}

  React.useEffect(() => {
    setIsMounted(true);
    setIsLoggedIn(localStorage.getItem('isLoggedIn') === 'true' || status === 'authenticated');
    const email = session?.user?.email || localStorage.getItem('userEmail') || '';
    setUserEmail(email);
    const storedUserId = localStorage.getItem('wpUserId') || '';
    setUserId(storedUserId);
    setUserName(localStorage.getItem('userName') || email.split('@')[0] || 'User');
    setUserImage(localStorage.getItem('userImage') || (session?.user as any)?.image || '');
    const cleanEmail = email.toLowerCase().trim();
    const sessionRole = (session as any)?.userRole;
    let initialRole = sessionRole || localStorage.getItem('userRole') || null;
    if (cleanEmail === 'ako.biganishvili@gmail.com' || storedUserId === '1' || (session as any)?.wpUserId === 1) {
      initialRole = 'administrator';
      localStorage.setItem('userRole', 'administrator');
      document.cookie = `userRole=administrator; path=/; max-age=604800`;
    } else if (localStorage.getItem('companyId') || localStorage.getItem('userRole') === 'company' || sessionRole === 'company') {
      initialRole = 'company';
      localStorage.setItem('userRole', 'company');
      document.cookie = `userRole=company; path=/; max-age=604800`;
    }
    setUserRole(initialRole);

    const fetchProfile = async () => {
      let token: any = localStorage.getItem('wp_jwt_token');
      const wpUserId = localStorage.getItem('wpUserId') || (session as any)?.wpUserId;

      if (status === 'authenticated' && !token) {
        token = (session as any)?.wpJwt || {};
      }
      if ((session as any)?.wpUserId && !localStorage.getItem('wpUserId')) {
        const sId = String((session as any).wpUserId);
        if (sId && sId !== 'undefined' && sId !== 'null') {
          localStorage.setItem('wpUserId', sId);
          document.cookie = `wpUserId=${sId}; path=/; max-age=604800`;
        }
      }

      if (token && wpUserId && wpUserId !== 'undefined' && wpUserId !== 'null') {
        try {
          const res = await getUserProfile(token, wpUserId);
          if (res && res.success) {
            const fName = res.first_name || res.meta?.first_name?.[0] || '';
            const lName = res.last_name || res.meta?.last_name?.[0] || '';
            const fullName = `${fName} ${lName}`.trim();
            if (fullName) {
              setUserName(fullName);
              localStorage.setItem('userName', fullName);
            }

            const avatar = res.customAvatar || res.meta?.customAvatar?.[0] || res.avatar_urls?.['96'] || '';
            if (avatar) {
              setUserImage(avatar);
              localStorage.setItem('userImage', avatar);
            }

            if (res.roles && res.roles.length > 0) {
              setUserRole(res.roles[0]);
              localStorage.setItem('userRole', res.roles[0]);
              document.cookie = `userRole=${res.roles[0]}; path=/; max-age=604800`;
            }
            if (res.companyId) {
              localStorage.setItem('companyId', String(res.companyId));
            }
            if (res.companyName) {
              localStorage.setItem('companyName', res.companyName);
            }
          }
        } catch (e) {
          console.error(e);
        }
      }

      // Automatically verify if this author has saved candidates or a registered company
      if (wpUserId && wpUserId !== 'undefined' && wpUserId !== 'null' && wpUserId !== '1' && initialRole !== 'administrator') {
        try {
          const uRes = await fetch(`https://admin.myjobs.al/wp-json/myjobs/v1/user-data?user_id=${wpUserId}&user_email=${encodeURIComponent(email)}`, { cache: 'no-store' });
          if (uRes.ok) {
            const uData = await uRes.json();
            if (uData.saved_candidates && uData.saved_candidates !== '[]' && uData.saved_candidates !== '') {
              setUserRole('company');
              localStorage.setItem('userRole', 'company');
              document.cookie = `userRole=company; path=/; max-age=604800`;
            }
          }
        } catch (e) {}

        try {
          const { getCompanyByAuthor } = await import('@/app/actions/companyActions');
          const compRes = await getCompanyByAuthor(Number(wpUserId), email);
          if (compRes.success && compRes.company) {
            setUserRole('company');
            localStorage.setItem('userRole', 'company');
            document.cookie = `userRole=company; path=/; max-age=604800`;
            localStorage.setItem('companyId', String(compRes.company.id));
            if (compRes.company.companyName) localStorage.setItem('companyName', compRes.company.companyName);
            if (compRes.company.logoBase64) localStorage.setItem('companyLogo', compRes.company.logoBase64);
          }
        } catch (ce) {}
      }
    };
    if (localStorage.getItem('isLoggedIn') === 'true' || status === 'authenticated') {
      fetchProfile();
    }

    window.addEventListener('profileUpdated', fetchProfile);

    // Fetch Notifications
    const fetchNotifications = async () => {
      if (localStorage.getItem('isLoggedIn') !== 'true' && status !== 'authenticated') return;

      const currentUserId = localStorage.getItem('wpUserId') || 'guest';
      const role = localStorage.getItem('userRole') || '';
      
      if (role === 'company' && currentUserId !== 'guest') {
        import('@/app/actions/applicationActions').then(({ getUnreadApplications }) => {
          getUnreadApplications(parseInt(currentUserId)).then((apps: any) => {
            const appsList = Array.isArray(apps) ? apps : [];
            const dismissedStr = localStorage.getItem('myjobs_dismissed_apps') || '[]';
            let dismissed: string[] = [];
            try { dismissed = JSON.parse(dismissedStr); } catch (e) {}
            const filteredApps = appsList.filter(app => app && !dismissed.includes(String(app.id)));
            setUnreadApplications(filteredApps);
            setUnreadCount(filteredApps.length);
          }).catch(console.error);
        });

        // Fetch user claims
        const authorId = localStorage.getItem('wpUserId') || (session as any)?.wpUserId;
        if (authorId) {
          import('@/app/actions/companyActions').then(({ getUserCompanyClaims }) => {
            if (getUserCompanyClaims) {
              getUserCompanyClaims(parseInt(authorId)).then((res: any) => {
                if (res.success && Array.isArray(res.data)) {
                  const processedClaims = res.data.filter((c: any) => c.acf?.status === 'APPROVED' || c.acf?.status === 'REJECTED' || c.acf?.status === 'PENDING');
                  const dismissedStr = localStorage.getItem('myjobs_dismissed_user_claims') || '[]';
                  let dismissed: string[] = [];
                  try { dismissed = JSON.parse(dismissedStr); } catch(err){}
                  const unreadClaims = processedClaims.filter((c: any) => !dismissed.includes(String(c.id)));
                  setUserClaimNotifications(unreadClaims);
                }
              }).catch((err: any) => console.error('Failed to fetch user claims:', err));
            }
          });
        }
      } else {
        const savedSubs = localStorage.getItem(`myjobs_subscribed_companies_${currentUserId}`);
        if (savedSubs) {
          try {
            const subs = JSON.parse(savedSubs);
            const companyNames = subs.map((s: any) => s.name);
            if (companyNames.length > 0) {
              const lastChecked = localStorage.getItem(`myjobs_last_notifications_check_${currentUserId}`);
              const res = await getRecentJobsFromSubscribedCompanies(companyNames, lastChecked || undefined);
              if (res.success && res.data) {
                setNotifications(res.data);
                setUnreadCount(res.data.length);
              }
            }
          } catch (e) {
            console.error(e);
          }
        }
      }
      
      if (role === 'administrator') {
        import('@/app/actions/companyActions').then(({ getCompanyClaims }) => {
          if (getCompanyClaims) {
            getCompanyClaims().then((res: any) => {
              if (res.success && Array.isArray(res.data)) {
                const pendingClaims = res.data.filter((c: any) => c.acf?.status === 'PENDING');
                const dismissedStr = localStorage.getItem('myjobs_dismissed_claims') || '[]';
                let dismissed: string[] = [];
                try { dismissed = JSON.parse(dismissedStr); } catch(err){}
                
                const unreadAdminClaims = pendingClaims.filter((c: any) => !dismissed.includes(String(c.id)));
                setAdminNotifications(unreadAdminClaims);
              }
            }).catch((err: any) => console.error('Failed to fetch claims:', err));
          }
        });
      }
    };
    fetchNotifications();

    return () => {
      window.removeEventListener('profileUpdated', fetchProfile);
    };
  }, [status, session]);

  const handleLogout = async () => {
    try {
      sessionStorage.setItem('logging_out', 'true');
    } catch (e) {}

    setIsLoggedIn(false);
    setUserRole(null);

    try {
      if (status === 'authenticated') {
        await signOut({ redirect: false });
      }
    } catch (e) {
      console.error('SignOut error:', e);
    }

    const authKeys = [
      'isLoggedIn', 'wpUserId', 'userEmail', 'userName', 'userRole', 'userImage',
      'wp_jwt_token', 'companyId', 'companyName', 'companyLogo', 'companyProfileComplete'
    ];
    authKeys.forEach((k) => localStorage.removeItem(k));

    // Clean any guest or cached subscription keys
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith('myjobs_') || k.startsWith('savedVacancies_') || k.startsWith('sentVacancies_') || k.startsWith('savedCandidates_')) {
          localStorage.removeItem(k);
        }
      });
    } catch (e) {}

    const cookiesToClear = [
      'userRole', 'userEmail', 'wpUserId', 'wp_jwt_token', 'companyId',
      'next-auth.session-token', '__Secure-next-auth.session-token',
      'next-auth.csrf-token', '__Host-next-auth.csrf-token', 'next-auth.callback-url'
    ];
    cookiesToClear.forEach((name) => {
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      try {
        document.cookie = `${name}=; path=/; domain=${window.location.hostname}; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      } catch (e) {}
    });

    window.location.replace(`/${language}`);
  };

  const profileMenuContent = (
    <>
      <div className={styles.profileHeader} style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', borderBottom: '1px solid #f3f4f6', paddingBottom: '0.75rem', marginBottom: '0.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {userEmail}
          </div>
          <button className={styles.mobileCloseBtn} onClick={() => setProfileDropdownOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.25rem' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>
        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
          {t('User ID')}: <span style={{ fontWeight: 600, color: '#4b5563' }}>{userId || (typeof window !== 'undefined' ? localStorage.getItem('wpUserId') : '') || '3'}</span>
        </div>
        <div style={{ fontSize: '0.78rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <span>{t('Registered as:')}</span>
          <span style={{
            fontWeight: 600,
            color: userRole === 'administrator' ? '#6366f1' : userRole === 'company' ? '#1c7bff' : '#111827',
            backgroundColor: userRole === 'administrator' ? '#eef2ff' : userRole === 'company' ? '#eff6ff' : '#f3f4f6',
            padding: '0.15rem 0.5rem',
            borderRadius: '0.375rem',
            border: `1px solid ${userRole === 'administrator' ? '#c7d2fe' : userRole === 'company' ? '#dbeafe' : '#e5e7eb'}`
          }}>
            {userRole === 'administrator' ? t('Administrator') : userRole === 'company' ? t('Company') : t('Job Seeker')}
          </span>
        </div>
      </div>

      <div style={{ padding: '0.25rem 0' }}>
        <Link href={`/${language}/profile`} onClick={() => setProfileDropdownOpen(false)} className={styles.profileDropdownLink}>{t('My profile')}</Link>
        <Link href={`/${language}/create-cv`} onClick={() => setProfileDropdownOpen(false)} className={styles.profileDropdownLink}>
          {t('Create CV')} <span style={{ background: '#1c7bff', color: 'white', fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: '1rem', marginLeft: '0.5rem' }}>{t('Free')}</span>
        </Link>
        <Link href={`/${language}/vacancy-saved`} onClick={() => setProfileDropdownOpen(false)} className={styles.profileDropdownLink}>{t('Saved vacancies')}</Link>
        {userRole === 'company' && (
          <Link href={`/${language}/saved-candidates`} onClick={() => setProfileDropdownOpen(false)} className={styles.profileDropdownLink}>{t('Saved candidates')}</Link>
        )}
        {userRole !== 'company' && (
          <Link href={`/${language}/vacancy-applied`} onClick={() => setProfileDropdownOpen(false)} className={styles.profileDropdownLink}>{t('Applied vacancies')}</Link>
        )}
        <Link href={`/${language}/subscribed`} onClick={() => setProfileDropdownOpen(false)} className={styles.profileDropdownLink}><TranslatableLabel translationKey="Followed companies" /></Link>
        <Link href={`/${language}/notifications`} onClick={() => setProfileDropdownOpen(false)} className={styles.profileDropdownLink}>{t('Notifications')}</Link>

        {userRole === 'subscriber' && (
          <div style={{ marginTop: '0.5rem', borderTop: '1px solid #f3f4f6', paddingTop: '0.5rem' }}>
            <Link href={`/${language}/company-upgrade`} onClick={() => setProfileDropdownOpen(false)} className={styles.profileDropdownLink} style={{ color: '#1c7bff', fontWeight: 500, display: 'flex', alignItems: 'center' }}>
              <span style={{
                display: 'inline-block',
                width: '6px',
                height: '6px',
                backgroundColor: '#1c7bff',
                borderRadius: '50%',
                marginRight: '8px'
              }}></span>
              {t('Company account')}
            </Link>
          </div>
        )}
      </div>

      <div className={styles.profileLogoutWrapper}>
        <button onClick={handleLogout} className={styles.profileDropdownLink} style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer' }}>
          {t('Log out')}
        </button>
      </div>
    </>
  );

  return (
    <>
      <nav className={styles.stickyTop}>
        <div className={`container ${styles.topRow}`}>
          <Link href={`/${language}`} className={styles.logo}>
            <img src="/logo.png?v=2" alt="myjobs.al" style={{ height: '37px', width: 'auto' }} />
          </Link>



          <div className={`${styles.actions} ${isMobileMenuOpen ? styles.actionsMobileOpen : ''}`}>
            <div className={styles.mobileOnlyLinks}>
              <Link href={`/${language}/vacancies`} onClick={() => setIsMobileMenuOpen(false)}>{t('Vacancies')}</Link>
              <Link href={`/${language}/companies`} onClick={() => setIsMobileMenuOpen(false)}>{t('Companies')}</Link>
              {isMounted && userRole === 'administrator' && (
                <Link href={`/${language}/candidates`} onClick={() => setIsMobileMenuOpen(false)}>{t('Candidates')}</Link>
              )}
              <Link href={`/${language}/create-cv`} onClick={() => setIsMobileMenuOpen(false)}>{t('Create CV')}</Link>
            </div>

            {isMounted && !isLoggedIn && (
              <button
                className={styles.companyBtn}
                onClick={() => router.push(`/${language}/register?type=company`)}
                style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 600 }}
              >
                <span style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#1d4ed8',
                  borderRadius: '50%',
                  marginRight: '6px'
                }}></span>
                {t('Company account')}
              </button>
            )}

            {isMounted && isLoggedIn && (userRole === 'company' || userRole === 'administrator') && (
              <button
                className={styles.companyBtn}
                onClick={() => router.push(`/${language}/dashboard/`)}
                style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 600 }}
              >
                <span style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#1d4ed8',
                  borderRadius: '50%',
                  marginRight: '6px'
                }}></span>
                {t('Company account')}
              </button>
            )}

            <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '8px', padding: '2px', marginLeft: '1rem', marginRight: '0.5rem' }}>
              <button
                onClick={() => setLanguage('en')}
                style={{ 
                  padding: '4px 8px', 
                  fontSize: '0.75rem', 
                  fontWeight: 600, 
                  border: 'none', 
                  borderRadius: '6px', 
                  backgroundColor: language === 'en' ? '#0b5cd5' : 'transparent', 
                  color: language === 'en' ? 'white' : '#6b7280', 
                  cursor: 'pointer', 
                  transition: 'all 0.2s' 
                }}
              >EN</button>
              <button
                onClick={() => setLanguage('sq')}
                style={{ 
                  padding: '4px 8px', 
                  fontSize: '0.75rem', 
                  fontWeight: 600, 
                  border: 'none', 
                  borderRadius: '6px', 
                  backgroundImage: language === 'sq' ? 'linear-gradient(rgba(243, 244, 246, 0), rgba(243, 244, 246, 0)), url(/alb.jpg)' : 'linear-gradient(rgba(243, 244, 246, 1), rgba(243, 244, 246, 1)), url(/alb.jpg)', 
                  backgroundSize: 'cover', 
                  backgroundPosition: 'center', 
                  color: language === 'sq' ? 'white' : '#6b7280', 
                  cursor: 'pointer', 
                  transition: 'all 0.2s' 
                }}
              >SQ</button>
            </div>

            {isMounted && (isLoggedIn ? (
              <div className={styles.authWrapper} style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginLeft: '1rem' }}>
                <div ref={notifRef} className={`${styles.hideInMobileMenu} hide-on-mobile`} style={{ position: 'relative' }}>
                  <button
                    onClick={() => {
                      setNotificationsOpen(!notificationsOpen);
                      if (!notificationsOpen) {
                        if (userRole !== 'company') {
                          setUnreadCount(0);
                        }
                        const currentUserId = localStorage.getItem('wpUserId') || 'guest';
                        localStorage.setItem(`myjobs_last_notifications_check_${currentUserId}`, new Date().toISOString());
                      }
                    }}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', position: 'relative' }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>
                    {(() => {
                      const totalUnreadCount = unreadCount + adminNotifications.length + userClaimNotifications.length;
                      if (totalUnreadCount > 0) {
                        return (
                          <span style={{
                            position: 'absolute',
                            top: '-6px',
                            right: '-8px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            borderRadius: '10px',
                            minWidth: '18px',
                            height: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 4px',
                            border: '2px solid white'
                          }}>
                            {totalUnreadCount}
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </button>

                  {notificationsOpen && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: '-100%',
                      marginTop: '1rem',
                      width: '380px',
                      background: 'white',
                      borderRadius: '0.75rem',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #f3f4f6',
                      zIndex: 100,
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{
                        padding: '1.25rem 1.5rem',
                        borderBottom: '1px solid #f3f4f6',
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '1.05rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span>Notifications</span>
                        {userRole !== 'company' && notifications.length > 0 && <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 400, cursor: 'pointer' }} onClick={() => setNotifications([])}>Clear all</span>}
                      </div>
                      
                      {userRole === 'company' || userRole === 'administrator' ? (
                        <div style={{ padding: (unreadApplications.length + adminNotifications.length + userClaimNotifications.length) > 0 ? '0' : '1.5rem', textAlign: 'center', color: '#6b7280', fontSize: '0.9rem', display: 'flex', flexDirection: 'column' }}>
                          {(unreadApplications.length + adminNotifications.length + userClaimNotifications.length) > 0 ? (
                            <>
                              <div style={{ maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                                {unreadApplications.map(app => (
                                  <Link 
                                    key={app.id}
                                    href={`/${language}/dashboard/resumes/received`} 
                                    onClick={(e) => {
                                      const dismissedStr = localStorage.getItem('myjobs_dismissed_apps') || '[]';
                                      let dismissed: string[] = [];
                                      try { dismissed = JSON.parse(dismissedStr); } catch(err){}
                                      dismissed.push(String(app.id));
                                      localStorage.setItem('myjobs_dismissed_apps', JSON.stringify(dismissed));
                                      
                                      const filtered = unreadApplications.filter(a => a.id !== app.id);
                                      setUnreadApplications(filtered);
                                      setNotificationsOpen(false);
                                    }}
                                    style={{ 
                                      display: 'flex', flexDirection: 'column', 
                                      padding: '1rem 1.5rem', borderBottom: '1px solid #f3f4f6', 
                                      textDecoration: 'none', color: '#111827', textAlign: 'left',
                                      transition: 'background-color 0.2s'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                  >
                                    <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                      New application for: {(() => {
                                        const title = app.title?.rendered || '';
                                        if (title.includes(' | ')) {
                                          return title.split(' | ')[1];
                                        }
                                        return app.acf?.job_title || `#${app.acf?.vacancy_id}`;
                                      })()}
                                    </span>
                                    <span style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.25rem' }}>Applicant: {app.acf?.applicant_email || app.acf?.applicant_name || 'View details'}</span>
                                  </Link>
                                ))}
                                
                                {adminNotifications.map(claim => (
                                  <Link 
                                    key={claim.id}
                                    href={`/${language}/dashboard/admin-claims`} 
                                    onClick={(e) => {
                                      const dismissedStr = localStorage.getItem('myjobs_dismissed_claims') || '[]';
                                      let dismissed: string[] = [];
                                      try { dismissed = JSON.parse(dismissedStr); } catch(err){}
                                      dismissed.push(String(claim.id));
                                      localStorage.setItem('myjobs_dismissed_claims', JSON.stringify(dismissed));
                                      
                                      const filtered = adminNotifications.filter(c => String(c.id) !== String(claim.id));
                                      setAdminNotifications(filtered);
                                      setNotificationsOpen(false);
                                    }}
                                    style={{ 
                                      display: 'flex', flexDirection: 'column', 
                                      padding: '1rem 1.5rem', borderBottom: '1px solid #f3f4f6', 
                                      textDecoration: 'none', color: '#111827', textAlign: 'left',
                                      backgroundColor: '#fffbeb',
                                      transition: 'background-color 0.2s'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fef3c7'}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fffbeb'}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <span style={{ backgroundColor: '#f59e0b', width: '8px', height: '8px', borderRadius: '50%' }}></span>
                                      <span style={{ fontWeight: 600, fontSize: '0.95rem', color: '#92400e' }}>
                                        Pending Company Claim
                                      </span>
                                    </div>
                                    <span style={{ fontSize: '0.85rem', color: '#92400e', marginTop: '0.25rem' }}>{claim.title?.rendered || `Claim #${claim.id}`}</span>
                                  </Link>
                                ))}

                                {userClaimNotifications.map(claim => {
                                  const status = claim.acf?.status;
                                  const isApproved = status === 'APPROVED';
                                  const isRejected = status === 'REJECTED';
                                  
                                  const bgColor = isApproved ? '#f0fdf4' : isRejected ? '#fef2f2' : '#fffbeb';
                                  const hoverColor = isApproved ? '#dcfce7' : isRejected ? '#fee2e2' : '#fef3c7';
                                  const dotColor = isApproved ? '#16a34a' : isRejected ? '#dc2626' : '#f59e0b';
                                  const textColor = isApproved ? '#166534' : isRejected ? '#991b1b' : '#92400e';

                                  return (
                                    <div 
                                      key={claim.id}
                                      role="button"
                                      onClick={(e) => {
                                        setSelectedClaim(claim);
                                        setNotificationsOpen(false);
                                      }}
                                      style={{ 
                                        display: 'flex', flexDirection: 'column', 
                                        padding: '1rem 1.5rem', borderBottom: '1px solid #f3f4f6', 
                                        textDecoration: 'none', color: '#111827', textAlign: 'left',
                                        backgroundColor: bgColor,
                                        transition: 'background-color 0.2s',
                                        cursor: 'pointer'
                                      }}
                                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = hoverColor}
                                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = bgColor}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <span style={{ backgroundColor: dotColor, width: '8px', height: '8px', borderRadius: '50%' }}></span>
                                        <span style={{ fontWeight: 600, fontSize: '0.95rem', color: textColor }}>
                                          {isApproved ? 'Claim Approved' : isRejected ? 'Claim Rejected' : 'Claim Under Review'}
                                        </span>
                                      </div>
                                      <span style={{ fontSize: '0.85rem', color: textColor, marginTop: '0.25rem' }}>
                                        Your claim for {claim.title?.rendered || `Claim #${claim.id}`} is {isApproved ? 'approved' : isRejected ? 'rejected' : 'under review'}.
                                        {isRejected && claim.acf?.rejection_reason && (
                                          <span style={{ display: 'block', marginTop: '0.25rem', fontWeight: 500, fontStyle: 'italic' }}>
                                            Reason: {claim.acf.rejection_reason.length > 50 ? claim.acf.rejection_reason.substring(0, 50) + '...' : claim.acf.rejection_reason}
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                              <div style={{ 
                                padding: '0.75rem 1.25rem', 
                                borderTop: '1px solid #f3f4f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                backgroundColor: '#ffffff',
                                borderBottomLeftRadius: '0.75rem',
                                borderBottomRightRadius: '0.75rem'
                              }}>
                                <Link 
                                  href={`/${language}/notifications`}
                                  onClick={() => setNotificationsOpen(false)}
                                  style={{
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                    color: 'var(--color-primary, #6366f1)',
                                    textDecoration: 'none',
                                    transition: 'opacity 0.2s'
                                  }}
                                  onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
                                  onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                                >
                                  {language === 'sq' ? 'Shiko të gjitha' : 'View all'}
                                </Link>
                                <button 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const dismissedAppsStr = localStorage.getItem('myjobs_dismissed_apps') || '[]';
                                    let dismissedApps: string[] = [];
                                    try { dismissedApps = JSON.parse(dismissedAppsStr); } catch(err){}
                                    unreadApplications.forEach(app => {
                                      if (!dismissedApps.includes(String(app.id))) {
                                        dismissedApps.push(String(app.id));
                                      }
                                    });
                                    localStorage.setItem('myjobs_dismissed_apps', JSON.stringify(dismissedApps));

                                    const dismissedClaimsStr = localStorage.getItem('myjobs_dismissed_claims') || '[]';
                                    let dismissedClaims: string[] = [];
                                    try { dismissedClaims = JSON.parse(dismissedClaimsStr); } catch(err){}
                                    adminNotifications.forEach(c => {
                                      if (!dismissedClaims.includes(String(c.id))) {
                                        dismissedClaims.push(String(c.id));
                                      }
                                    });
                                    localStorage.setItem('myjobs_dismissed_claims', JSON.stringify(dismissedClaims));

                                    const dismissedUserClaimsStr = localStorage.getItem('myjobs_dismissed_user_claims') || '[]';
                                    let dismissedUserClaims: string[] = [];
                                    try { dismissedUserClaims = JSON.parse(dismissedUserClaimsStr); } catch(err){}
                                    userClaimNotifications.forEach(c => {
                                      if (!dismissedUserClaims.includes(String(c.id))) {
                                        dismissedUserClaims.push(String(c.id));
                                      }
                                    });
                                    localStorage.setItem('myjobs_dismissed_user_claims', JSON.stringify(dismissedUserClaims));
                                    
                                    setUnreadApplications([]);
                                    setAdminNotifications([]);
                                    setUserClaimNotifications([]);
                                    setUnreadCount(0);
                                  }}
                                  style={{
                                    padding: '0.35rem 0.85rem',
                                    backgroundColor: '#f3f4f6',
                                    color: '#4b5563',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                    transition: 'background-color 0.2s'
                                  }}
                                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                >
                                  {language === 'sq' ? 'Pastro të gjitha' : 'Clear all'}
                                </button>
                              </div>
                            </>
                          ) : (
                            <div style={{ padding: '2rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                              <span>{language === 'sq' ? 'Nuk u gjet asnjë njoftim' : 'No notifications found'}</span>
                              <Link 
                                href={`/${language}/notifications`}
                                onClick={() => setNotificationsOpen(false)}
                                style={{
                                  fontSize: '0.85rem',
                                  fontWeight: 500,
                                  color: 'var(--color-primary, #6366f1)',
                                  textDecoration: 'none'
                                }}
                              >
                                {language === 'sq' ? 'Shiko të gjitha njoftimet' : 'View all notifications'}
                              </Link>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <div style={{
                            maxHeight: '350px',
                            overflowY: 'auto',
                            padding: notifications.length === 0 ? '3rem 1.5rem' : '0'
                          }}>
                            {notifications.length === 0 ? (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151', fontSize: '1rem', fontWeight: 500 }}>
                                {language === 'sq' ? 'Nuk u gjet asnjë njoftim' : 'No notifications found'}
                              </div>
                            ) : (
                              notifications.map((notif: any) => (
                                <Link href={`/${language}/vacancies/${notif.id}`} key={notif.id} onClick={() => setNotificationsOpen(false)} style={{
                                  display: 'flex',
                                  padding: '1rem 1.5rem',
                                  borderBottom: '1px solid #f3f4f6',
                                  textDecoration: 'none',
                                  gap: '1rem',
                                  transition: 'background-color 0.2s'
                                }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                  {notif.companyLogo ? (
                                    <img src={notif.companyLogo} alt={notif.companyName} style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }} />
                                  ) : (
                                    <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#6b7280' }}>
                                      {notif.companyName?.substring(0, 2).toUpperCase() || 'C'}
                                    </div>
                                  )}
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <span style={{ color: '#111827', fontWeight: 500, fontSize: '0.95rem' }}>{notif.companyName} {language === 'sq' ? 'postoi një njoftim të ri' : 'posted a new job'}</span>
                                    <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>{notif.title}</span>
                                  </div>
                                </Link>
                              ))
                            )}
                          </div>
                          <div style={{ 
                            padding: '0.75rem 1.25rem', 
                            borderTop: '1px solid #f3f4f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: '#ffffff',
                            borderBottomLeftRadius: '0.75rem',
                            borderBottomRightRadius: '0.75rem'
                          }}>
                            <Link 
                              href={`/${language}/notifications`}
                              onClick={() => setNotificationsOpen(false)}
                              style={{
                                fontSize: '0.85rem',
                                fontWeight: 500,
                                color: 'var(--color-primary, #6366f1)',
                                textDecoration: 'none',
                                transition: 'opacity 0.2s'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
                              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                            >
                              {language === 'sq' ? 'Shiko të gjitha' : 'View all'}
                            </Link>
                            {notifications.length > 0 && (
                              <button 
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setNotifications([]);
                                }}
                                style={{
                                  padding: '0.35rem 0.85rem',
                                  backgroundColor: '#f3f4f6',
                                  color: '#4b5563',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  fontWeight: 500,
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                              >
                                {language === 'sq' ? 'Pastro të gjitha' : 'Clear all'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Link 
                  href={`/${language}/vacancy-saved`} 
                  className={styles.hideInMobileMenu} 
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', position: 'relative' }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
                  {savedVacancies.length > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-8px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      borderRadius: '10px',
                      minWidth: '18px',
                      height: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px',
                      border: '2px solid white'
                    }}>
                      {savedVacancies.length}
                    </span>
                  )}
                </Link>

                <div ref={profileRef} style={{ position: 'relative' }}>
                  <button
                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                    className={`${styles.profileBtn} ${styles.hideInMobileMenu}`}
                    style={{
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '2rem',
                      padding: '0.25rem 0.75rem 0.25rem 0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                      {userImage || session?.user?.image ? (
                        <img src={userImage || session?.user?.image || ''} alt={userName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="#9ca3af"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" /></svg>
                      )}
                    </div>
                    <span style={{ fontWeight: 500, fontSize: '0.875rem', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</span>
                    <span style={{ fontSize: '0.7rem' }}>▼</span>
                  </button>

                  <div className={`${styles.profileDropdown} ${profileDropdownOpen ? styles.openOnDesktop : ''}`}>
                    {profileMenuContent}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Link href={`/${language}/login`} className={styles.loginBtn} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                  <span className={styles.userIcon}>👤</span> {t('Log in')}
                </Link>

                <Link href={`/${language}/register`} className={styles.loginBtn} style={{ textDecoration: 'none', background: '#0b5cd5', color: 'white', border: 'none' }}>
                  {t('Register')}
                </Link>
              </>
            ))}
          </div>

          <div className={styles.mobileIconsWrapper}>
            {isMounted && isLoggedIn && (
              <>
                <button
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'none', alignItems: 'center' }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                </button>

                <Link href={`/${language}/vacancy-saved`} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', position: 'relative' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                  </svg>
                  {savedVacancies.length > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-6px',
                      right: '-8px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      borderRadius: '10px',
                      minWidth: '18px',
                      height: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px',
                      border: '2px solid white'
                    }}>
                      {savedVacancies.length}
                    </span>
                  )}
                </Link>
              </>
            )}

            <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '8px', padding: '2px' }}>
              <button
                onClick={() => setLanguage('en')}
                style={{ 
                  padding: '4px 8px', 
                  fontSize: '0.75rem', 
                  fontWeight: 600, 
                  border: 'none', 
                  borderRadius: '6px', 
                  backgroundColor: language === 'en' ? '#0b5cd5' : 'transparent', 
                  color: language === 'en' ? 'white' : '#6b7280', 
                  cursor: 'pointer', 
                  transition: 'all 0.2s' 
                }}
              >EN</button>
              <button
                onClick={() => setLanguage('sq')}
                style={{ 
                  padding: '4px 8px', 
                  fontSize: '0.75rem', 
                  fontWeight: 600, 
                  border: 'none', 
                  borderRadius: '6px', 
                  backgroundImage: language === 'sq' ? 'linear-gradient(rgba(243, 244, 246, 0), rgba(243, 244, 246, 0)), url(/alb.jpg)' : 'linear-gradient(rgba(243, 244, 246, 1), rgba(243, 244, 246, 1)), url(/alb.jpg)', 
                  backgroundSize: 'cover', 
                  backgroundPosition: 'center', 
                  color: language === 'sq' ? 'white' : '#6b7280', 
                  cursor: 'pointer', 
                  transition: 'all 0.2s' 
                }}
              >SQ</button>
            </div>

            <div ref={mobileProfileRef} style={{ position: 'relative' }}>
              {isMounted && (isLoggedIn ? (
                <>
                  <button aria-label={t('Profile Menu')} onClick={() => setProfileDropdownOpen(!profileDropdownOpen)} style={{ padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#edf8ff', color: '#0b5cd5', overflow: 'hidden', border: userImage ? '2px solid #0b5cd5' : 'none' }}>
                    {userImage ? (
                      <img src={userImage} alt={userName || 'User'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                    )}
                  </button>
                  <div className={`${styles.mobileProfileDropdown} ${profileDropdownOpen ? styles.open : styles.closed}`}>
                    {profileMenuContent}
                  </div>
                </>
              ) : (
                <Link href={`/${language}/login`} aria-label={t('Login')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#edf8ff', color: '#0b5cd5', overflow: 'hidden' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </nav>

      <div className={styles.bottomWrapper}>
        <div className={`container ${styles.bottomRow}`}>
          <ul className={styles.navLinks}>
            <li><Link href={`/${language}/vacancies`}>{t('Vacancies')}</Link></li>
            <li><Link href={`/${language}/companies`}>{t('Companies')}</Link></li>
            {isMounted && userRole === 'administrator' && (
              <li><Link href={`/${language}/candidates`}>{t('Candidates')}</Link></li>
            )}
            <li><Link href={`/${language}/create-cv`}>{t('Create CV')}</Link></li>
          </ul>
        </div>
      </div>
      
      {selectedClaim && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '2rem',
            maxWidth: '500px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            position: 'relative'
          }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginTop: 0, marginBottom: '1.5rem', color: '#111827' }}>Claim Details</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6', paddingBottom: '0.5rem' }}>
                <strong style={{ color: '#6b7280' }}>Company:</strong> 
                <span style={{ color: '#111827', fontWeight: 500 }}>{selectedClaim.title?.rendered ? (selectedClaim.title.rendered.match(/(?:Instant Claim|Claim) for (.+?) by /)?.[1] || selectedClaim.title.rendered) : `Claim #${selectedClaim.id}`}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6', paddingBottom: '0.5rem' }}>
                <strong style={{ color: '#6b7280' }}>Status:</strong> 
                <span style={{ 
                  fontWeight: 600,
                  color: selectedClaim.acf?.status === 'APPROVED' ? '#16a34a' : selectedClaim.acf?.status === 'REJECTED' ? '#dc2626' : '#d97706'
                }}>{selectedClaim.acf?.status}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6', paddingBottom: '0.5rem' }}>
                <strong style={{ color: '#6b7280' }}>Requested On:</strong> 
                <span style={{ color: '#111827' }}>{new Date(selectedClaim.date).toLocaleDateString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6', paddingBottom: '0.5rem' }}>
                <strong style={{ color: '#6b7280' }}>Method:</strong> 
                <span style={{ color: '#111827' }}>{selectedClaim.acf?.verification_method || 'N/A'}</span>
              </div>
              
              {selectedClaim.acf?.status === 'REJECTED' && selectedClaim.acf?.rejection_reason && (
                <div style={{ backgroundColor: '#fef2f2', padding: '1rem', borderRadius: '8px', borderLeft: '4px solid #ef4444', marginTop: '1rem' }}>
                  <strong style={{ display: 'block', color: '#991b1b', marginBottom: '0.25rem' }}>Reason for rejection:</strong>
                  <span style={{ color: '#b91c1c', lineHeight: 1.5 }}>{selectedClaim.acf.rejection_reason}</span>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setSelectedClaim(null)}
                style={{ padding: '0.5rem 1.25rem', borderRadius: '6px', backgroundColor: '#f3f4f6', color: '#4b5563', border: 'none', fontWeight: 500, cursor: 'pointer' }}
              >
                Close
              </button>
              <button 
                onClick={() => {
                  const dismissedStr = localStorage.getItem('myjobs_dismissed_user_claims') || '[]';
                  let dismissed: string[] = [];
                  try { dismissed = JSON.parse(dismissedStr); } catch(err){}
                  dismissed.push(String(selectedClaim.id));
                  localStorage.setItem('myjobs_dismissed_user_claims', JSON.stringify(dismissed));
                  
                  const filtered = userClaimNotifications.filter(c => String(c.id) !== String(selectedClaim.id));
                  setUserClaimNotifications(filtered);
                  setSelectedClaim(null);
                }}
                style={{ padding: '0.5rem 1.25rem', borderRadius: '6px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', fontWeight: 500, cursor: 'pointer' }}
              >
                Clean
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
